import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { of, filter, map, switchMap, take, combineLatest, Observable } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

import { AuthService, AuthStorageService } from '@spartacus/core';
import { PageSlotModule } from '@spartacus/storefront';

import { CoreModalService, SdBorderWrapperComponent, SdTabComponent, SdTabGroupComponent } from '@seb/web-design-system/core';
import { SisCategoriesComponent } from '../../components/static/sis-categories/sis-categories.component';
import { StoreLocatorComponent } from '../../components/static/store-locator/store-locator.component';
import { AbstractPageComponent } from '../../templates/abstract-page/abstract-page.component';
import { FaqEntry, FaqJsonLd, FaqVpcCmsParagraphComponentInterface } from '../../models';
import { CustomJsonLdDirective, IFrameResizerDirective } from '../../directives';
import { BaseStoreService, UserUidService } from '../../services';
import { FAQMapper } from '../../mappers';

const URL_TO_LABEL = {
  '/faq/csc/products': 'FAQ',
  '/instructions-for-use': 'IFU',
  '/consumer-services/warranty-and-repairs/repairers': 'REPAIR',
  '/contact-form': 'CONTACT',
  '/faq-forfait': 'FORFAIT'
} as const;

@Component({
  selector: 'wc-service-page',
  templateUrl: './service-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PageSlotModule,
    SdBorderWrapperComponent,
    StoreLocatorComponent,
    SdTabGroupComponent,
    SdTabComponent,
    SisCategoriesComponent,
    IFrameResizerDirective,
    TranslatePipe,
    CustomJsonLdDirective
  ],
  providers: [CoreModalService]
})
export class ServicePageComponent extends AbstractPageComponent implements OnInit {
  iframeUrl = signal<SafeResourceUrl>('');
  label = signal<string>('');

  protected readonly jsonLdFaqs = signal<FaqJsonLd>(undefined);

  private readonly baseStoreService = inject(BaseStoreService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);
  private readonly userUidService = inject(UserUidService);
  private readonly authStorageService = inject(AuthStorageService);
  private readonly faqMapper = inject(FAQMapper);

  constructor() {
    super();

    this.snippetsOptions = { images: true };
    this.eagerLoadingSlot = 'DynamicBody';
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subscriptions.add(
      this.cmsService.getCurrentPage().pipe(
        filter((p) => !!p)
      ).subscribe(({ label }) => {
        this.label.set(this.convertLabel(label));

        if (this.label() === 'CONTACT') {
          this.getIframeUrl();
        }

        if (label === '/faq-forfait') {
          this.loadFaqs('FaqWithTransport');
        } else {
          this.loadFaqs('DynamicBody');
        }
      })
    );
  }

  private loadFaqs(slotId: string): void {
    this.subscriptions.add(
      this.getFaqs(slotId).subscribe((faqResults) => {
        this.generateFaqsSnippet(faqResults);
      })
    );
  }

  private generateFaqsSnippet(faqResults) {
    if (!faqResults.length) {
      this.jsonLdFaqs.set(undefined);
      return;
    };
    const faqs: FaqEntry[] = faqResults.flatMap((faq) =>
      faq?.faqvpccollection ? Object.values(faq.faqvpccollection) : []
    );
    this.jsonLdFaqs.set(this.faqMapper.toJsonLdFaqs(faqs));
  }

  private getFaqs(slotId: string): Observable<FaqVpcCmsParagraphComponentInterface[]> {
    return this.cmsService.getContentSlot(slotId).pipe(
      switchMap((slot) => {
        const faqComponents = slot?.components?.filter(({ typeCode }) => typeCode === 'FAQVPCCMSParagraphCmp') ?? [];

        if (faqComponents.length === 0) return of([]);

        return combineLatest(
          faqComponents.map(({ uid }) =>
            this.cmsService.getComponentData(uid).pipe(
              filter((cmp: any) => !!cmp?.faqvpccollection),
            )
          )
        );
      })
    );
  }

  private getIframeUrl(): void {
    const accessToken$ = this.authStorageService.getToken().pipe(
      map(({ access_token }) => access_token),
      take(1)
    );

    const rcuAccountToken$ = this.authService.isUserLoggedIn().pipe(
      switchMap((isUserLoggedIn) => isUserLoggedIn ? (
        accessToken$.pipe(
          switchMap((token) => this.userUidService.getUserToken(token))
        )
      ) : of([]))
    );

    this.subscriptions.add(
      rcuAccountToken$.subscribe((rcuAccountToken) => {
        const { contactFormURL } = this.baseStoreService.baseStore;
        let contactUrl = contactFormURL ?? '';

        if (rcuAccountToken?.length) {
          contactUrl += `&token=${rcuAccountToken}`;
        }

        this.iframeUrl.set(this.domSanitizer.bypassSecurityTrustResourceUrl(contactUrl));
      })
    );
  }

  private convertLabel(url: string): string {
    return URL_TO_LABEL[url] ?? '';
  }
}
